import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getTokens, infoDelegacion } from '../lib/api';
import { mensajeErrorRed } from '../lib/erroresRed';
import { useStore } from '../store/useStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { fmtFull, initials } from '../lib/format';
import { Palette, PenLine, ShieldCheck, MonitorSmartphone, Loader2, Trash2, KeyRound, BellRing, Filter, Plus, Camera } from 'lucide-react';
import { registerPasskey, passkeySupported } from '../lib/webauthn';
import { nombreDispositivo } from '../lib/nombreDispositivo';
import { subscribePush, unsubscribePush, isPushSubscribed, pushSupported } from '../lib/push';
import SignatureManager from '../components/SignatureManager';
import { DialogoConfirmar } from '../components/ui/confirmar';
import { useIsMobile } from '../hooks/useIsMobile';

/* Cómo se lee una regla en castellano. Antes la lista enseñaba los nombres
   internos ("from", "skip_inbox") y no había forma de saber qué hacía cada una
   sin abrirla. */
const CAMPO_TEXTO = {
  from: 'el remitente', to: 'el destinatario', subject: 'el asunto',
  body: 'el cuerpo', any: 'el asunto o el cuerpo',
};
const ACCION_TEXTO = {
  skip_inbox: 'saltar la bandeja (a Todos)', move: 'mover a', trash: 'ir a la papelera',
  star: 'destacar', mark_read: 'marcar como leído',
};

const TABS = [
  { value: 'general', label: 'General', icon: Palette, testid: 'tab-general' },
  { value: 'signature', label: 'Firma', icon: PenLine, testid: 'tab-signature' },
  { value: 'security', label: 'Seguridad', icon: ShieldCheck, testid: 'tab-security' },
  { value: 'rules', label: 'Reglas', icon: Filter, testid: 'tab-rules' },
  { value: 'sessions', label: 'Sesiones', icon: MonitorSmartphone, testid: 'tab-sessions' },
];

/* Fila de ajuste. Vive FUERA de Settings (A23): definida dentro, cada render creaba un componente
   nuevo, React desmontaba y volvía a montar el Switch y el foco del teclado se iba a <body>. */
const Row = ({ title, desc, children }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap">
    <div className="min-w-0 flex-1" style={{ minWidth: 180 }}>
      <div className="font-medium break-words">{title}</div>
      {desc && <div className="text-sm text-muted-foreground break-words">{desc}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function Settings() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const { user, setUser, theme, setTheme, trasMover, setTrasMover } = useStore();
  const s = user?.settings || {};
  // En el buzón de otra persona (acceso delegado) no se tocan sus credenciales ni sus sesiones (S1).
  const delegado = !!infoDelegacion();
  const [signature, setSignature] = useState(s.signature || '');
  const [language, setLanguage] = useState(s.language || 'es');
  const [vacationOn, setVacationOn] = useState(!!s.vacation_enabled);
  const [vacationMsg, setVacationMsg] = useState(s.vacation_message || '');
  const [vacationSubject, setVacationSubject] = useState(s.vacation_subject || '');
  const [vacationStart, setVacationStart] = useState(s.vacation_start || '');
  const [vacationEnd, setVacationEnd] = useState(s.vacation_end || '');
  const [vacationInternos, setVacationInternos] = useState(!!s.vacation_solo_internos);
  // Lo que había al abrir (o al guardar): se compara contra esto y no contra `user.settings`, que
  // AppShell refresca del servidor y daría «cambios sin guardar» sin que nadie tocara nada.
  const formulario = {
    signature, language, vacationOn, vacationMsg, vacationSubject, vacationStart, vacationEnd, vacationInternos,
  };
  const [guardado, setGuardado] = useState(formulario);
  const cambiosPendientes = Object.keys(formulario).some((k) => formulario[k] !== guardado[k]);
  const [saving, setSaving] = useState(false);

  // 2FA
  const [twoFA, setTwoFA] = useState(null);
  const [code, setCode] = useState('');

  // Passkeys + push + reglas
  const [pkBusy, setPkBusy] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [rule, setRule] = useState({
    name: '', field: 'from', op: 'contains', value: '',
    action: 'move', action_value: 'archive', apply_existing: false,
  });
  // Qué campo de la regla falta (F5) y si ya se está guardando (F7)
  const [errorRegla, setErrorRegla] = useState({});
  const [guardandoRegla, setGuardandoRegla] = useState(false);
  const [verificando2FA, setVerificando2FA] = useState(false);
  // Confirmación pendiente: { titulo, mensaje, boton, accion }
  const [confirmar, setConfirmar] = useState(null);

  // Foto de perfil (se sirve autenticada, así que va como blob)
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  const { data: sessions = [] } = useQuery({ queryKey: ['sessions'], queryFn: async () => (await api.get('/auth/sessions')).data });
  const { data: accounts = [] } = useQuery({ queryKey: ['accounts'], queryFn: async () => (await api.get('/accounts')).data });
  const { data: passkeys = [] } = useQuery({ queryKey: ['passkeys'], queryFn: async () => (await api.get('/webauthn/credentials')).data });
  const { data: rules = [] } = useQuery({ queryKey: ['rules'], queryFn: async () => (await api.get('/rules')).data });
  /* Carpetas de verdad del buzón, no una lista fija: si el destino ofrecido no
     existe, la regla se guarda y luego no hace nada, que es la peor forma de
     fallar (silenciosa). */
  const cuentaReglas = accounts?.[0]?.id;
  const { data: arbolReglas } = useQuery({
    queryKey: ['mail-folders', cuentaReglas],
    queryFn: async () => (await api.get(`/mail/folders?account_id=${cuentaReglas}`)).data,
    enabled: !!cuentaReglas,
    staleTime: 120000,
  });
  const carpetasDestino = useMemo(() => {
    const sistema = new Set(['inbox', 'sent', 'drafts', 'trash', 'spam', 'archive']);
    return (arbolReglas?.flat || []).filter(
      (f) => f.selectable !== false && !f.logical
        && !sistema.has((f.name || '').toLowerCase())
        && !sistema.has((f.label || '').toLowerCase()),
    );
  }, [arbolReglas]);
  const nombreCarpeta = (raw) =>
    carpetasDestino.find((f) => f.name === raw)?.label
    || ({ archive: 'Todos', spam: 'Spam', trash: 'Papelera' }[raw] || raw);

  useEffect(() => { if (pushSupported()) isPushSubscribed().then(setPushOn).catch(() => {}); }, []);

  const loadPhoto = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await api.get(`/users/photo?email=${encodeURIComponent(user.email)}`, { responseType: 'blob' });
      setPhotoUrl(URL.createObjectURL(res.data));
    } catch {
      setPhotoUrl(null); // 404 = todavía sin foto
    }
  }, [user?.email]);
  useEffect(() => { loadPhoto(); }, [loadPhoto]);
  // Libera el object URL anterior al cambiar y al desmontar.
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) { toast.error('Formato no admitido. Usa PNG, JPG o WebP.'); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error('La imagen supera 15 MB'); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post('/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadPhoto();
      qc.invalidateQueries({ queryKey: ['unified'] });
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la foto');
    } finally { setPhotoBusy(false); }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.delete('/me/photo');
      setPhotoUrl(null);
      qc.invalidateQueries({ queryKey: ['unified'] });
      toast.success('Foto eliminada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar la foto');
    } finally { setPhotoBusy(false); }
  };

  const addPasskey = async () => {
    setPkBusy(true);
    try {
      await registerPasskey(nombreDispositivo());
      qc.invalidateQueries({ queryKey: ['passkeys'] });
      toast.success('Passkey registrada');
    } catch (err) {
      toast.error(err.response?.data?.detail || (err.name === 'NotAllowedError' ? 'Operación cancelada' : 'No se pudo registrar la passkey'));
    } finally { setPkBusy(false); }
  };
  const delPasskey = async (id) => {
    try {
      await api.delete(`/webauthn/credentials/${id}`);
      toast.success('Passkey eliminada');
    } catch (err) { toast.error(mensajeErrorRed(err, 'eliminar la passkey')); }
    qc.invalidateQueries({ queryKey: ['passkeys'] });
  };
  // Un clic borraba la passkey sin preguntar (A12): quita un acceso, así que se confirma.
  const pedirBorrarPasskey = (pk) => setConfirmar({
    titulo: '¿Eliminar esta passkey?',
    mensaje: `«${pk.name}» dejará de servir para entrar. Podrás seguir entrando con tu correo y contraseña, y registrar otra cuando quieras.`,
    boton: 'Eliminar passkey',
    accion: () => delPasskey(pk.id),
  });

  const togglePush = async (v) => {
    setPushBusy(true);
    try {
      if (v) { await subscribePush(); setPushOn(true); toast.success('Notificaciones push activadas'); }
      else { await unsubscribePush(); setPushOn(false); toast.success('Notificaciones push desactivadas'); }
    } catch (err) {
      toast.error(err.message || 'No se pudo cambiar las notificaciones');
    } finally { setPushBusy(false); }
  };

  /* El error vive JUNTO al campo que falta, no en un aviso que se desvanece sin decir cuál de
     los dos era. `guardandoRegla` evita que dos clics creen dos reglas iguales. */
  const addRule = async () => {
    const faltan = { name: !rule.name.trim(), value: !rule.value.trim() };
    if (faltan.name || faltan.value) { setErrorRegla(faltan); return; }
    setErrorRegla({});
    if (guardandoRegla) return;
    setGuardandoRegla(true);
    try {
      const payload = { ...rule, action_value: rule.action === 'skip_inbox' ? 'archive' : rule.action_value };
      const r = await api.post('/rules', payload);
      qc.invalidateQueries({ queryKey: ['rules'] });
      qc.invalidateQueries({ queryKey: ['unified'] });
      setRule({ name: '', field: 'from', op: 'contains', value: '', action: 'move', action_value: 'archive', apply_existing: false });
      const applied = r.data?.applied_existing || 0;
      toast.success(applied > 0 ? `Regla creada · ${applied} mensaje(s) existentes aplicados` : 'Regla creada');
    } catch { toast.error('Error al crear la regla'); } finally { setGuardandoRegla(false); }
  };
  const delRule = async (id) => {
    try {
      await api.delete(`/rules/${id}`);
      toast.success('Regla eliminada');
    } catch (err) { toast.error(mensajeErrorRed(err, 'eliminar la regla')); }
    qc.invalidateQueries({ queryKey: ['rules'] });
  };
  const pedirBorrarRegla = (r) => setConfirmar({
    titulo: '¿Eliminar la regla?',
    mensaje: `«${r.name}» dejará de aplicarse al correo que entre. El correo que ya ordenó se queda donde está.`,
    boton: 'Eliminar regla',
    accion: () => delRule(r.id),
  });
  const runRules = async () => {
    const acc = accounts.find((a) => a.is_default) || accounts[0];
    if (!acc) { toast.error('No hay cuenta de correo'); return; }
    try { const r = await api.post(`/rules/run?account_id=${acc.id}`); toast.success(`Reglas aplicadas: ${r.data.applied}`); }
    catch { toast.error('Error al ejecutar reglas'); }
  };

  const saveSettings = async () => {
    if (vacationOn && !vacationMsg.trim()) { toast.error('Escribe el mensaje de la respuesta automática'); return; }
    if (vacationStart && vacationEnd && vacationEnd < vacationStart) { toast.error('La fecha de fin es anterior a la de inicio'); return; }
    setSaving(true);
    try {
      const res = await api.put('/settings', {
        signature, language, theme, vacation_enabled: vacationOn, vacation_message: vacationMsg,
        vacation_subject: vacationSubject, vacation_start: vacationStart, vacation_end: vacationEnd,
        vacation_solo_internos: vacationInternos,
      });
      setUser({ ...user, settings: res.data });
      setGuardado(formulario);
      toast.success('Ajustes guardados');
    } catch (err) { toast.error(mensajeErrorRed(err, 'guardar los ajustes')); } finally { setSaving(false); }
  };

  const [preparando2FA, setPreparando2FA] = useState(false);
  const setup2FA = async () => {
    setPreparando2FA(true);
    try {
      const res = await api.post('/auth/2fa/setup');
      setTwoFA(res.data);
    } catch (err) {
      toast.error(mensajeErrorRed(err, 'preparar la verificación en dos pasos'));   // antes fallaba en silencio (A14)
    } finally { setPreparando2FA(false); }
  };
  const enable2FA = async () => {
    if (verificando2FA) return;
    setVerificando2FA(true);
    try { await api.post('/auth/2fa/enable', { code }); setUser({ ...user, totp_enabled: true }); setTwoFA(null); setCode(''); toast.success('2FA activado'); }
    catch (err) { toast.error(err.response?.status === 400 ? 'Código incorrecto. Usa el más reciente de tu app.' : mensajeErrorRed(err, 'activar el 2FA')); }
    finally { setVerificando2FA(false); }
  };
  // Apagarlo pide un código vigente (S6, auditoría 2026-10-05): con la sesión sin vencimiento, un token
  // robado bastaba para quitarlo sin dejar rastro.
  const [codigoApagar, setCodigoApagar] = useState(null);   // null = diálogo cerrado
  const disable2FA = async () => {
    try {
      await api.post('/auth/2fa/disable', { code: (codigoApagar || '').trim() });
      setUser({ ...user, totp_enabled: false });
      setCodigoApagar(null);
      toast.success('2FA desactivado');
    } catch (err) {
      // Antes no había try/catch: si el servidor decía que no, la interfaz enseñaba el 2FA
      // apagado igualmente y la cuenta seguía pidiéndolo al entrar.
      toast.error(err.response?.data?.detail || 'No se pudo desactivar el segundo factor');
    }
  };
  /* Quitar el segundo factor es la acción de más impacto de esta pantalla: era un clic sin
     confirmación y sin control de errores. */
  const pedirDesactivar2FA = () => setCodigoApagar('');
  const revokeSession = async (sess) => {
    try {
      await api.delete(`/auth/sessions/${sess.id}`);
    } catch (err) { toast.error(mensajeErrorRed(err, 'cerrar la sesión')); qc.invalidateQueries({ queryKey: ['sessions'] }); return; }
    // La de este navegador: se sale ya, no una hora después cuando caduque el acceso (S8).
    if (sess.refresh_jti && sess.refresh_jti === jtiPropio) {
      useStore.getState().logout();
      window.location.assign('/login');
      return;
    }
    toast.success('Sesión cerrada. Ese dispositivo dejará de tener acceso en menos de una hora.');
    qc.invalidateQueries({ queryKey: ['sessions'] });
  };
  // Qué sesión es ESTE navegador (A13): el jti del token de refresco guardado aquí.
  const jtiPropio = (() => {
    try {
      const t = getTokens().refresh;
      return t ? JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).jti : null;
    } catch (_) { return null; }
  })();
  const pedirCerrarSesion = (sess) => setConfirmar({
    titulo: sess.refresh_jti === jtiPropio ? '¿Cerrar la sesión de este dispositivo?' : '¿Cerrar esta sesión?',
    mensaje: sess.refresh_jti === jtiPropio
      ? 'Es la sesión desde la que estás ahora: saldrás y tendrás que volver a entrar.'
      : `${sess.device || 'Ese dispositivo'} tendrá que volver a entrar con contraseña.`,
    boton: 'Cerrar sesión',
    accion: () => revokeSession(sess),
  });

  /* Fila de ajuste: envuelve en móvil en vez de forzar ancho. */
  return (
    <div className="h-full overflow-y-auto overflow-x-hidden od-scroll">
      <div className="w-full max-w-3xl mx-auto p-4 sm:p-6 min-w-0">
        <h1 className="font-display text-xl sm:text-2xl font-bold mb-5">Ajustes</h1>
        <Tabs defaultValue="general" className="w-full min-w-0">
          {/* El desbordamiento vive DENTRO de la tira, nunca en la página. */}
          <TabsList className="od-tabstrip mb-5 flex w-full max-w-full justify-start gap-1 overflow-x-auto">
            {TABS.filter((t) => !(delegado && (t.value === 'security' || t.value === 'sessions'))).map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  data-testid={t.testid}
                  className={isMobile ? 'px-2.5 text-xs shrink-0' : 'shrink-0'}
                >
                  {!isMobile && <Icon className="h-4 w-4 mr-2" />}
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="general" className="space-y-4 min-w-0">
            <Card className="p-4 sm:p-5">
              <div className="font-medium mb-3">Foto de perfil</div>
              <div className="flex items-center gap-4 flex-wrap">
                <div
                  className="shrink-0 grid place-items-center overflow-hidden"
                  style={{
                    width: 72, height: 72, borderRadius: '50%',
                    background: photoUrl ? 'transparent' : 'var(--accent-soft)',
                    color: 'var(--accent)', border: '1px solid var(--line)',
                    fontSize: 22, fontWeight: 600,
                  }}
                  data-testid="profile-photo"
                >
                  {photoUrl
                    ? <img src={photoUrl} alt="Foto de perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initials(user?.name, user?.email)}
                </div>
                <div className="min-w-0 flex-1" style={{ minWidth: 180 }}>
                  <div className="text-sm text-muted-foreground break-words mb-2">
                    Se muestra en tus correos internos. PNG, JPG o WebP · máx 15 MB.
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="od-file-oculto"
                      tabIndex={-1}
                      aria-hidden="true"
                      onChange={onPickPhoto}
                      data-testid="profile-photo-input"
                    />
                    <Button size="sm" onClick={() => fileRef.current?.click()} disabled={photoBusy} className="gap-2" data-testid="profile-photo-upload">
                      {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}Subir foto
                    </Button>
                    {photoUrl && (
                      <Button size="sm" variant="outline" onClick={removePhoto} disabled={photoBusy} data-testid="profile-photo-remove">
                        Quitar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4 sm:p-5 space-y-4">
              <Row title="Tema oscuro" desc="Cambia la apariencia de la aplicación">
                <Switch checked={theme === 'dark'} onCheckedChange={(v) => setTheme(v ? 'dark' : 'light')} data-testid="theme-switch" />
              </Row>
              <Row title="Idioma">
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-36 max-w-full"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="es">Español</SelectItem><SelectItem value="en" disabled>English (aún no disponible)</SelectItem></SelectContent>
                </Select>
              </Row>
              {/* M32: se guarda al momento, como el tema */}
              <Row title="Después de archivar o eliminar" desc="Qué ves al sacar un correo abierto de la carpeta">
                <Select value={trasMover} onValueChange={setTrasMover}>
                  <SelectTrigger className="w-44 max-w-full" data-testid="tras-mover" aria-label="Después de archivar o eliminar"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lista">Volver a la lista</SelectItem>
                    <SelectItem value="siguiente">Abrir el siguiente</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
            </Card>

            <Card className="p-4 sm:p-5">
              <div className="font-medium mb-1">Respuesta automática</div>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <span className="text-sm text-muted-foreground min-w-0 flex-1 break-words" style={{ minWidth: 180 }}>
                  Contesta por ti mientras no estás, aunque no tengas el portal ni la app abiertos.
                  Una vez al día por remitente; nunca a listas de correo ni a direcciones automáticas.
                </span>
                <Switch checked={vacationOn} onCheckedChange={setVacationOn} data-testid="vacation-switch" className="shrink-0" />
              </div>
              {vacationOn && (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="vac-ini">Desde (opcional)</Label>
                      <Input id="vac-ini" type="date" value={vacationStart} onChange={(e) => setVacationStart(e.target.value)} data-testid="vacation-start" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="vac-fin">Hasta (opcional)</Label>
                      <Input id="vac-fin" type="date" value={vacationEnd} min={vacationStart || undefined} onChange={(e) => setVacationEnd(e.target.value)} data-testid="vacation-end" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vac-asunto">Asunto</Label>
                    <Input id="vac-asunto" value={vacationSubject} onChange={(e) => setVacationSubject(e.target.value)} placeholder="Respuesta automática: (asunto original)" maxLength={200} data-testid="vacation-subject" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vac-msg">Mensaje</Label>
                    <Textarea id="vac-msg" value={vacationMsg} onChange={(e) => setVacationMsg(e.target.value)} placeholder="Estoy fuera de la oficina hasta el… Para asuntos urgentes escribe a…" rows={4} className="max-w-full" maxLength={4000} />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch checked={vacationInternos} onCheckedChange={setVacationInternos} data-testid="vacation-internos" />
                    Contestar solo a personas de mi organización
                  </label>
                </div>
              )}
            </Card>

            {/* El tema se aplica al momento y el resto no (A16): con cambios pendientes, el botón se
                queda pegado abajo y lo dice, para no salir creyendo que la respuesta automática ya está activa. */}
            <div
              className={cambiosPendientes ? 'sticky bottom-0 z-10 -mx-4 sm:mx-0 px-4 py-3 flex flex-wrap items-center gap-3 sm:rounded-xl' : ''}
              style={cambiosPendientes ? { background: 'var(--panel-2)', borderTop: '1px solid var(--line)', boxShadow: '0 -4px 12px rgba(20,22,45,.06)' } : undefined}
            >
              {cambiosPendientes && (
                <span role="status" data-testid="settings-pendientes" className="text-sm font-medium flex-1 min-w-[160px]">Tienes cambios sin guardar</span>
              )}
              <Button onClick={saveSettings} disabled={saving} data-testid="settings-save" className="w-full sm:w-auto">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Guardar cambios
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="signature" className="min-w-0">
            <Card className="p-4 sm:p-5 space-y-3 min-w-0">
              <div>
                <div className="font-medium">Firmas de correo</div>
                <div className="text-sm text-muted-foreground break-words">
                  Crea varias firmas (formal, breve, interna…), sube imágenes y asígnalas a cada cuenta. Se añaden automáticamente al redactar.
                </div>
              </div>
              <div className="min-w-0 overflow-x-hidden"><SignatureManager accounts={accounts} /></div>
            </Card>
          </TabsContent>

          {!delegado && <TabsContent value="security" className="min-w-0">
            <Card className="p-4 sm:p-5">
              <Row
                title="Autenticación en dos pasos (2FA)"
                desc={user?.totp_enabled ? 'Activada: tu cuenta está protegida' : 'Añade una capa extra de seguridad'}
              >
                {user?.totp_enabled
                  ? <Button variant="outline" onClick={pedirDesactivar2FA} data-testid="disable-2fa">Desactivar</Button>
                  : <Button onClick={setup2FA} disabled={preparando2FA} data-testid="setup-2fa">{preparando2FA && <Loader2 className="h-4 w-4 animate-spin" />}Activar 2FA</Button>}
              </Row>
            </Card>

            <Card className="p-4 sm:p-5 mt-4">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-start gap-3 min-w-0 flex-1" style={{ minWidth: 180 }}>
                  <KeyRound className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium">Passkeys</div>
                    <div className="text-sm text-muted-foreground break-words">Inicia sesión sin contraseña con tu huella, rostro o PIN del dispositivo</div>
                  </div>
                </div>
                {passkeySupported() && (
                  <Button onClick={addPasskey} disabled={pkBusy} className="gap-2 shrink-0" data-testid="add-passkey">
                    {pkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Añadir
                  </Button>
                )}
              </div>
              {!passkeySupported() ? (
                <div className="text-sm text-muted-foreground">Tu navegador no soporta passkeys.</div>
              ) : passkeys.length === 0 ? (
                <div className="text-sm text-muted-foreground py-3 text-center border border-dashed border-border rounded-xl">Aún no tienes passkeys registradas.</div>
              ) : (
                <div className="space-y-2">
                  {passkeys.map((pk) => (
                    <div key={pk.id} className="flex items-center gap-3 p-3 rounded-xl border border-border min-w-0" data-testid="passkey-row">
                      <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{pk.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{fmtFull(pk.created_at)}</div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => pedirBorrarPasskey(pk)} data-testid="delete-passkey" aria-label={`Eliminar la passkey ${pk.name}`} title="Eliminar passkey"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-4 sm:p-5 mt-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1" style={{ minWidth: 180 }}>
                  <BellRing className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium">Notificaciones push</div>
                    <div className="text-sm text-muted-foreground break-words">Avisos de correo nuevo aunque salgas de la página o cierres el navegador</div>
                  </div>
                </div>
                <Switch checked={pushOn} disabled={pushBusy || !pushSupported()} onCheckedChange={togglePush} data-testid="push-switch" className="shrink-0" />
              </div>
              {!pushSupported() && <div className="text-xs text-muted-foreground mt-2">Tu navegador no soporta notificaciones push.</div>}
              {pushSupported() && typeof Notification !== 'undefined' && Notification.permission === 'denied' && (
                <div className="text-xs text-destructive mt-2 break-words">Permiso bloqueado en el navegador. Actívalo en la configuración del sitio y recarga.</div>
              )}
              {pushSupported() && pushOn && (
                <div className="text-xs text-muted-foreground mt-2 break-words">Activo en este dispositivo. En iPhone/iPad hace falta añadir Orvian a la pantalla de inicio (PWA).</div>
              )}
            </Card>

            <Dialog open={codigoApagar !== null} onOpenChange={(v) => !v && setCodigoApagar(null)}>
              <DialogContent className="max-w-sm w-[calc(100vw-2rem)] sm:w-full" data-testid="dialogo-apagar-2fa">
                <DialogHeader><DialogTitle>¿Desactivar el segundo factor?</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Tu cuenta volverá a protegerse solo con la contraseña. Para confirmar que eres tú, escribe el código actual de tu app de autenticación.
                </p>
                <Label htmlFor="codigo-apagar-2fa">Código de verificación</Label>
                <Input
                  id="codigo-apagar-2fa"
                  value={codigoApagar || ''}
                  onChange={(e) => setCodigoApagar(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  data-testid="2fa-codigo-apagar"
                />
                <Button
                  onClick={disable2FA}
                  disabled={(codigoApagar || '').length < 6}
                  style={{ background: 'var(--danger)', color: '#fff' }}
                  data-testid="confirmar-apagar-2fa"
                >
                  Desactivar 2FA
                </Button>
              </DialogContent>
            </Dialog>

            <Dialog open={!!twoFA} onOpenChange={(v) => !v && setTwoFA(null)}>
              <DialogContent className="max-w-sm w-[calc(100vw-2rem)] sm:w-full">
                <DialogHeader><DialogTitle>Configurar 2FA</DialogTitle></DialogHeader>
                {/* QR + clave copiable + enlace directo en el teléfono (A14): antes había que teclear
                    unos 32 caracteres a mano. */}
                {twoFA?.qr ? (
                  <>
                    <p className="text-sm text-muted-foreground">Escanea este código con tu app de autenticación (Google Authenticator, Microsoft Authenticator…):</p>
                    <img src={twoFA.qr} alt="Código QR para la app de autenticación" className="mx-auto rounded-lg bg-white p-1" style={{ width: 196, height: 196 }} data-testid="2fa-qr" />
                    <p className="text-xs text-muted-foreground">¿No puedes escanearlo? Escribe esta clave en la app:</p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Escribe esta clave en tu app de autenticación (Google Authenticator, Microsoft Authenticator…):</p>
                )}
                <code className="block bg-muted p-3 rounded-lg text-center font-mono text-sm break-all">{twoFA?.secret}</code>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard?.writeText(twoFA?.secret || '')
                      .then(() => toast.success('Clave copiada'))
                      .catch(() => toast.error('No se pudo copiar: selecciónala y cópiala a mano'))}
                  >
                    Copiar clave
                  </Button>
                  {isMobile && twoFA?.otpauth_uri && (
                    <Button asChild variant="outline" size="sm">
                      <a href={twoFA.otpauth_uri}>Abrir en la app</a>
                    </Button>
                  )}
                </div>
                <Label htmlFor="codigo-2fa">Código de verificación</Label>
                {/* Mismo trato que en el acceso (Login.jsx): teclado numérico, relleno automático
                    del código que llega y nada de dejar enviar cuatro dígitos. */}
                <Input
                  id="codigo-2fa"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  data-testid="2fa-code"
                />
                <Button onClick={enable2FA} disabled={code.length < 6 || verificando2FA} data-testid="verify-2fa">
                  {verificando2FA && <Loader2 className="h-4 w-4 animate-spin" />}
                  Verificar y activar
                </Button>
              </DialogContent>
            </Dialog>
          </TabsContent>}

          <TabsContent value="rules" className="space-y-4 min-w-0">
            <Card className="p-4 sm:p-5 space-y-3 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2"><Filter className="h-4 w-4 text-primary shrink-0" />Reglas de correo</div>
                  <div className="text-sm text-muted-foreground break-words">Filtros tipo Gmail · organizan el correo entrante</div>
                </div>
                <Button variant="outline" size="sm" onClick={runRules} data-testid="run-rules" className="shrink-0">Ejecutar ahora</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                <div className="space-y-1.5 sm:col-span-2 min-w-0">
                  <Label htmlFor="rule-name">Nombre de la regla</Label>
                  <Input
                    id="rule-name"
                    value={rule.name}
                    onChange={(e) => { setRule({ ...rule, name: e.target.value }); if (errorRegla.name) setErrorRegla({ ...errorRegla, name: false }); }}
                    placeholder="Ej. Boletines a Archivo"
                    data-testid="rule-name"
                    aria-invalid={!!errorRegla.name}
                    aria-describedby={errorRegla.name ? 'rule-name-error' : undefined}
                  />
                  {errorRegla.name && (
                    <p id="rule-name-error" role="alert" className="text-xs text-destructive">Ponle un nombre para reconocerla después.</p>
                  )}
                </div>
                <div className="space-y-1.5 min-w-0"><Label htmlFor="rule-field">Si el campo</Label>
                  <Select value={rule.field} onValueChange={(v) => setRule({ ...rule, field: v })}><SelectTrigger id="rule-field" data-testid="rule-field" className="w-full"><SelectValue /></SelectTrigger><SelectContent>
                    <SelectItem value="from">Remitente</SelectItem>
                    <SelectItem value="to">Destinatario (Para o Cc)</SelectItem>
                    <SelectItem value="subject">Asunto</SelectItem>
                    <SelectItem value="body">Palabras en el cuerpo</SelectItem>
                    <SelectItem value="any">Asunto o cuerpo</SelectItem>
                  </SelectContent></Select>
                </div>
                <div className="space-y-1.5 min-w-0"><Label htmlFor="rule-op">Condición</Label>
                  <Select
                    value={['body', 'any'].includes(rule.field) ? 'contains' : rule.op}
                    onValueChange={(v) => setRule({ ...rule, op: v })}
                    disabled={['body', 'any'].includes(rule.field)}
                  >
                    <SelectTrigger id="rule-op" data-testid="rule-op" className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="contains">Contiene</SelectItem><SelectItem value="equals">Es igual a</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2 min-w-0">
                  <Label htmlFor="rule-value">{['body', 'any'].includes(rule.field) ? 'Palabras clave' : 'Valor'}</Label>
                  <Input
                    id="rule-value"
                    aria-invalid={!!errorRegla.value}
                    aria-describedby={errorRegla.value ? 'rule-value-error' : undefined}
                    value={rule.value}
                    onChange={(e) => { setRule({ ...rule, value: e.target.value }); if (errorRegla.value) setErrorRegla({ ...errorRegla, value: false }); }}
                    placeholder={['body', 'any'].includes(rule.field)
                      ? 'factura, cotización, orden de compra'
                      : (rule.field === 'to' ? 'ventas@tuempresa.com' : 'newsletter@empresa.com')}
                    data-testid="rule-value"
                  />
                  {errorRegla.value && (
                    <p id="rule-value-error" role="alert" className="text-xs text-destructive">
                      Escribe con qué tiene que coincidir el correo.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Puedes poner varias separadas por coma: basta con que coincida una.
                  </p>
                </div>
                <div className="space-y-1.5 min-w-0"><Label htmlFor="rule-action">Acción</Label>
                  <Select value={rule.action} onValueChange={(v) => setRule({ ...rule, action: v })}>
                    <SelectTrigger id="rule-action" data-testid="rule-action" className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="skip_inbox">Saltar bandeja (archivar en silencio)</SelectItem>
                      <SelectItem value="move">Mover a carpeta</SelectItem>
                      <SelectItem value="trash">Enviar a papelera</SelectItem>
                      <SelectItem value="star">Destacar</SelectItem>
                      <SelectItem value="mark_read">Marcar leído</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {rule.action === 'move' && (
                  <div className="space-y-1.5 min-w-0"><Label htmlFor="rule-folder">Carpeta destino</Label>
                    <Select value={rule.action_value} onValueChange={(v) => setRule({ ...rule, action_value: v })}>
                      <SelectTrigger id="rule-folder" data-testid="rule-folder" className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {carpetasDestino.map((f) => (
                          <SelectItem key={f.name} value={f.name}>
                            {'\u00a0'.repeat(Math.min(f.depth || 0, 3) * 2)}{f.label}
                          </SelectItem>
                        ))}
                        {/* Una sola vez: sin carpetas propias salía repetida y el selector pintaba «TodosTodos» (QA 2026-10-06) */}
                        <SelectItem value="archive">Todos</SelectItem>
                        <SelectItem value="spam">Spam</SelectItem>
                        <SelectItem value="trash">Papelera</SelectItem>
                      </SelectContent>
                    </Select>
                    {carpetasDestino.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Crea carpetas desde la barra lateral del correo para poder elegirlas aquí.
                      </p>
                    )}
                  </div>
                )}
                {rule.action === 'skip_inbox' && (
                  <div className="sm:col-span-2 text-xs text-muted-foreground rounded-lg border border-dashed p-3 break-words">
                    Los mensajes que coincidan irán directo a <strong>Archivo</strong> sin quedarse en Recibidos (estilo Gmail “Omitir la bandeja de entrada”).
                  </div>
                )}
                <label className="sm:col-span-2 flex items-start gap-3 p-3 rounded-xl border cursor-pointer min-w-0" data-testid="rule-apply-existing">
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={!!rule.apply_existing}
                    onChange={(e) => setRule({ ...rule, apply_existing: e.target.checked })}
                  />
                  <span className="min-w-0">
                    <span className="text-sm font-medium block break-words">Aplicar también a mensajes existentes</span>
                    <span className="text-xs text-muted-foreground break-words">Ejecuta esta regla ahora sobre la bandeja de entrada de tus cuentas</span>
                  </span>
                </label>
              </div>
              <Button onClick={addRule} disabled={guardandoRegla} className="gap-2 w-full sm:w-fit" data-testid="rule-save">
                {guardandoRegla ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Crear regla
              </Button>
            </Card>
            <Card className="p-4 sm:p-5 space-y-2 min-w-0">
              <div className="font-medium mb-1">Reglas activas ({rules.length})</div>
              {rules.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-xl">No hay reglas configuradas.</div>
              ) : rules.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl border border-border min-w-0" data-testid="rule-row">
                  <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      Si {CAMPO_TEXTO[r.field] || r.field}{' '}
                      {['body', 'any'].includes(r.field) ? 'incluye' : (r.op === 'equals' ? 'es' : 'contiene')}{' '}
                      &quot;{r.value}&quot; → {ACCION_TEXTO[r.action] || r.action}
                      {r.action === 'move' ? ` “${nombreCarpeta(r.action_value)}”` : ''}
                      {r.enabled === false ? ' · desactivada' : ''}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => pedirBorrarRegla(r)} aria-label={`Eliminar la regla ${r.name}`} title="Eliminar regla"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </Card>
          </TabsContent>

          {!delegado && <TabsContent value="sessions" className="min-w-0">
            <Card className="p-4 sm:p-5 space-y-3 min-w-0">
              <div className="font-medium">Sesiones activas</div>
              {sessions.filter((x) => x.refresh_jti !== jtiPropio).length > 1 && (
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" className="text-destructive" data-testid="cerrar-otras-sesiones"
                    onClick={() => setConfirmar({
                      titulo: '¿Cerrar todas las demás sesiones?',
                      mensaje: 'Esta se queda abierta. Los demás dispositivos tendrán que volver a entrar con contraseña.',
                      boton: 'Cerrar las demás',
                      accion: async () => {
                        const otras = sessions.filter((x) => x.refresh_jti !== jtiPropio);
                        let ok = 0;
                        for (const x of otras) {
                          try { await api.delete(`/auth/sessions/${x.id}`); ok += 1; } catch (_) { /* se cuenta abajo */ }
                        }
                        qc.invalidateQueries({ queryKey: ['sessions'] });
                        if (ok === otras.length) toast.success(`Se cerraron ${ok} sesiones`);
                        else toast.error(`Se cerraron ${ok} de ${otras.length} sesiones`);
                      },
                    })}
                  >
                    Cerrar todas las demás
                  </Button>
                </div>
              )}
              {sessions.map((sess) => (
                <div key={sess.id} className="flex items-center gap-3 p-3 rounded-xl border border-border min-w-0" data-testid="session-row">
                  <MonitorSmartphone className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      {sess.refresh_jti && sess.refresh_jti === jtiPropio && (
                        <Badge className="mr-2 align-middle" data-testid="session-actual">Este dispositivo</Badge>
                      )}
                      {/* Nombre legible en vez del user-agent crudo («Mozilla/5.0 (Windows…»): S5 */}
                      <span title={sess.device}>{sess.device_id ? `App Orvian Mail · ${nombreDispositivo(sess.device, 'teléfono')}` : nombreDispositivo(sess.device, 'Dispositivo sin identificar')}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {sess.ip && sess.ip !== '127.0.0.1' ? `${sess.ip} · ` : ''}Última actividad: {fmtFull(sess.last_seen)}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-destructive shrink-0" onClick={() => pedirCerrarSesion(sess)}
                    aria-label={`Cerrar la sesión de ${nombreDispositivo(sess.device, 'ese dispositivo')}`}>Cerrar</Button>
                </div>
              ))}
            </Card>
          </TabsContent>}
        </Tabs>
      </div>

      <DialogoConfirmar
        open={!!confirmar}
        titulo={confirmar?.titulo || ''}
        mensaje={confirmar?.mensaje || ''}
        boton={confirmar?.boton || 'Continuar'}
        testId="ajustes-confirmar"
        onOpenChange={(v) => !v && setConfirmar(null)}
        onConfirmar={() => { const a = confirmar?.accion; setConfirmar(null); a?.(); }}
      />
    </div>
  );
}
